/**
 * Veeva File Staging browser — the vault's "FTP" area, over the File Staging
 * REST API (same session as VQL). List folders/files, navigate, download,
 * upload, create folder, rename, delete. Writes are audited server-side
 * (`veeva_file.*`).
 */

import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Breadcrumbs,
  Button,
  FileButton,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconDownload,
  IconFileZip,
  IconFolder,
  IconFolderPlus,
  IconPencil,
  IconRefresh,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess, toastWarning } from "@/shared/ui/toast";
import {
  downloadFile,
  veevaStagingContentUrl,
  veevaStagingCreateFolder,
  veevaStagingDelete,
  veevaStagingDownloadAll,
  veevaStagingList,
  veevaStagingRename,
  veevaStagingUpload,
  type FileStagingItem,
} from "@/shared/api/integration-browse";

function parentOf(path: string): string {
  const trimmed = path.replace(/\/+$/, "");
  const idx = trimmed.lastIndexOf("/");
  return idx <= 0 ? "/" : trimmed.slice(0, idx);
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function VeevaFileStaging({ connectionId }: { connectionId: string }) {
  const getToken = useAccessToken();
  // null = "the user's staging home"; the backend returns the resolved path.
  const [path, setPath] = useState<string | null>(null);
  const [resolvedPath, setResolvedPath] = useState<string>("");
  const [items, setItems] = useState<FileStagingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const result = await veevaStagingList(token, connectionId, { path });
      setItems(result.items);
      setResolvedPath(result.resolved_path);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [getToken, connectionId, path]);

  useEffect(() => {
    void load();
  }, [load]);

  const onDownload = async (item: FileStagingItem) => {
    setBusy(true);
    try {
      const token = await getToken();
      await downloadFile(token, veevaStagingContentUrl(connectionId, item.path), item.name);
    } catch (err) {
      toastApiError("Download failed", err);
    } finally {
      setBusy(false);
    }
  };

  // Zip every file in the current folder (recursive), server-side + audited.
  const onDownloadAll = async () => {
    setBusy(true);
    try {
      const token = await getToken();
      const res = await veevaStagingDownloadAll(token, connectionId, path, true);
      if (res.count === 0) {
        toastWarning("Nothing to download", "This folder has no files.");
      } else {
        toastSuccess(
          "Download ready",
          res.truncated
            ? `Zipped the first ${res.count} of ${res.matched} files (capped).`
            : `Zipped ${res.count} file${res.count === 1 ? "" : "s"}.`,
        );
      }
    } catch (err) {
      toastApiError("Download all failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (file: File | null) => {
    if (!file || !resolvedPath) return;
    setBusy(true);
    try {
      const token = await getToken();
      const target = `${resolvedPath.replace(/\/+$/, "")}/${file.name}`;
      await veevaStagingUpload(token, connectionId, target, file, true);
      toastSuccess("Uploaded", `${file.name} → ${resolvedPath}`);
      await load();
    } catch (err) {
      toastApiError("Upload failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onNewFolder = async () => {
    if (!resolvedPath) return;
    const name = await promptForReason("New folder", "Folder name", {
      confirmLabel: "Create",
      plain: true,
      minLength: 1,
    });
    if (!name) return;
    setBusy(true);
    try {
      const token = await getToken();
      await veevaStagingCreateFolder(
        token,
        connectionId,
        `${resolvedPath.replace(/\/+$/, "")}/${name}`,
      );
      toastSuccess("Folder created", name);
      await load();
    } catch (err) {
      toastApiError("Create folder failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onRename = async (item: FileStagingItem) => {
    const newName = await promptForReason(`Rename "${item.name}"`, "New name", {
      confirmLabel: "Rename",
      plain: true,
      minLength: 1,
    });
    if (!newName) return;
    setBusy(true);
    try {
      const token = await getToken();
      await veevaStagingRename(token, connectionId, item.path, parentOf(item.path), newName);
      toastSuccess("Renamed", `${item.name} → ${newName}`);
      await load();
    } catch (err) {
      toastApiError("Rename failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (item: FileStagingItem) => {
    const reason = await promptForReason(`Delete "${item.name}"`, "Confirm — type a reason", {
      warning:
        item.kind === "folder"
          ? "Deletes the folder and everything under it. This cannot be undone."
          : "This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!reason) return;
    setBusy(true);
    try {
      const token = await getToken();
      await veevaStagingDelete(token, connectionId, item.path, reason, item.kind === "folder");
      toastWarning("Deleted", item.name);
      await load();
    } catch (err) {
      toastApiError("Delete failed", err);
    } finally {
      setBusy(false);
    }
  };

  // Breadcrumb segments from the resolved path.
  const segments = resolvedPath.replace(/^\/+/, "").split("/").filter(Boolean);
  const crumbs = segments.map((seg, i) => {
    const full = "/" + segments.slice(0, i + 1).join("/");
    return (
      <Anchor key={full} size="sm" onClick={() => setPath(full)}>
        {seg}
      </Anchor>
    );
  });

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Breadcrumbs>
          <Anchor size="sm" onClick={() => setPath(null)}>
            Home
          </Anchor>
          {crumbs}
        </Breadcrumbs>
        <Group gap="xs">
          {segments.length > 0 && (
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setPath(parentOf(resolvedPath))}
              disabled={busy || loading}
            >
              ↑ Up
            </Button>
          )}
          <Button
            variant="default"
            size="xs"
            leftSection={<IconRefresh size={16} />}
            onClick={() => void load()}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            variant="default"
            size="xs"
            leftSection={<IconFileZip size={16} />}
            onClick={() => void onDownloadAll()}
            disabled={busy || loading || items.length === 0}
          >
            Download all
          </Button>
          <Button
            variant="default"
            size="xs"
            leftSection={<IconFolderPlus size={16} />}
            onClick={() => void onNewFolder()}
            disabled={busy || !resolvedPath}
          >
            New folder
          </Button>
          <FileButton onChange={(f) => void onUpload(f)} disabled={busy || !resolvedPath}>
            {(props) => (
              <Button variant="default" size="xs" leftSection={<IconUpload size={16} />} {...props}>
                Upload
              </Button>
            )}
          </FileButton>
        </Group>
      </Group>

      {error && (
        <Alert variant="light" color="red" title="Could not list files">
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p={0}>
        {loading ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : items.length === 0 ? (
          <Text c="dimmed" p="xl" ta="center">
            This folder is empty.
          </Text>
        ) : (
          <Table verticalSpacing="sm" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th>Modified (UTC)</Table.Th>
                <Table.Th style={{ width: 120 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item) => (
                <Table.Tr key={item.path}>
                  <Table.Td>
                    {item.kind === "folder" ? (
                      <Anchor onClick={() => setPath(item.path)}>
                        <Group gap={6} wrap="nowrap">
                          <IconFolder size={16} />
                          {item.name}
                        </Group>
                      </Anchor>
                    ) : (
                      <Text size="sm">{item.name}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {item.kind === "folder" ? "—" : formatBytes(item.size)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {item.modified_date ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      {item.kind === "file" && (
                        <Tooltip label="Download">
                          <ActionIcon
                            variant="subtle"
                            color="brand"
                            onClick={() => void onDownload(item)}
                            aria-label={`Download ${item.name}`}
                          >
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Rename">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => void onRename(item)}
                          aria-label={`Rename ${item.name}`}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => void onDelete(item)}
                          aria-label={`Delete ${item.name}`}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}
